import React from 'react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { Clock, CheckCircle2, Package2, AlertTriangle } from 'lucide-react';
import { PalletSummaryDto } from '../../../../types/picking';

interface StatusBarProps {
  completedLines: number;
  totalLines: number;
  pallets: PalletSummaryDto[];
  activePalletSSCC: string | null;
  priority: number;
}

export const StatusBar: React.FC<StatusBarProps> = ({
  completedLines,
  totalLines,
  pallets,
  activePalletSSCC,
  priority
}) => {
  const { t } = useTranslation();
  const activePallet = pallets.find(p => p.sscc === activePalletSSCC);
  
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 sm:p-4 z-50">
      <div className="max-w-6xl mx-auto">
        {/* Mobile: Stack in 2 rows */}
        <div className="flex sm:hidden flex-col gap-2 text-xs">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Clock size={14} className="text-gray-500" />
              {new Date().toLocaleTimeString('no-NO')}
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle2 size={14} className="text-green-600" />
              {completedLines}/{totalLines}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Package2 size={14} className="text-purple-600" />
              {pallets.length} {t('warehouse.modules.picking.labels.totalPallets')}
            </span>
            {activePallet && (
              <span className="flex items-center gap-1 truncate max-w-[150px]">
                <span className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></span>
                <span className="truncate">{activePallet.sscc}</span>
              </span>
            )}
            <span className="flex items-center gap-1">
              <AlertTriangle size={14} className="text-orange-500" />
              {t('warehouse.modules.picking.labels.priority')}: {priority}
            </span>
          </div>
        </div>

        {/* Desktop: Single row */}
        <div className="hidden sm:flex items-center justify-between text-sm">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2">
              <Clock size={16} className="text-gray-500" />
              {new Date().toLocaleTimeString('no-NO')}
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle2 size={16} className="text-green-600" />
              {completedLines} {t('common.of')} {totalLines} {t('warehouse.modules.picking.labels.completed')}
            </span>
          </div>

          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2">
              <Package2 size={16} className="text-purple-600" />
              {pallets.length} {t('warehouse.modules.picking.labels.totalPallets')}
            </span>
            {activePallet && (
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                {t('warehouse.modules.picking.labels.activePallet', { '0': activePallet.sscc })}
              </span>
            )}
            <span className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-orange-500" />
              {t('warehouse.modules.picking.labels.priority')}: {priority}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};